import React, { useState, useEffect, useMemo } from 'react';

const GRID_SIZE = 12;
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// Generate a random letter
const randomLetter = () => LETTERS[Math.floor(Math.random() * LETTERS.length)];

// Try to place a word in the grid
const placeWord = (grid, word) => {
  const directions = [
    [0, 1],   // right
    [1, 0],   // down
    [1, 1],   // diagonal down-right
    [-1, 1],  // diagonal up-right
  ];
  
  const attempts = 100;
  for (let attempt = 0; attempt < attempts; attempt++) {
    const dir = directions[Math.floor(Math.random() * directions.length)];
    const startRow = Math.floor(Math.random() * GRID_SIZE);
    const startCol = Math.floor(Math.random() * GRID_SIZE);
    
    // Check if word fits
    const endRow = startRow + dir[0] * (word.length - 1);
    const endCol = startCol + dir[1] * (word.length - 1);
    
    if (endRow < 0 || endRow >= GRID_SIZE || endCol < 0 || endCol >= GRID_SIZE) {
      continue;
    }
    
    // Check if cells are empty or match
    let canPlace = true;
    for (let i = 0; i < word.length; i++) {
      const row = startRow + dir[0] * i;
      const col = startCol + dir[1] * i;
      const cell = grid[row][col];
      if (cell !== '' && cell !== word[i]) {
        canPlace = false;
        break;
      }
    }
    
    if (canPlace) {
      // Place the word
      for (let i = 0; i < word.length; i++) {
        const row = startRow + dir[0] * i;
        const col = startCol + dir[1] * i;
        grid[row][col] = word[i];
      }
      return true;
    }
  }
  return false;
};

// Generate a wordsearch grid with given words
const generateGrid = (words) => {
  // Initialize empty grid
  const grid = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(''));
  
  // Place words
  const placedWords = [];
  for (const word of words) {
    const cleanWord = word.toUpperCase().replace(/[^A-Z]/g, '');
    if (cleanWord.length >= 3 && cleanWord.length <= GRID_SIZE) {
      if (placeWord(grid, cleanWord)) {
        placedWords.push(cleanWord);
      }
    }
  }
  
  // Fill remaining cells with random letters
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (grid[row][col] === '') {
        grid[row][col] = randomLetter();
      }
    }
  }
  
  return { grid, placedWords };
};

export default function Wordsearch({ words = [], isVisible = false }) {
  const [puzzleKey, setPuzzleKey] = useState(0);
  
  // Regenerate puzzle when visibility changes to true
  useEffect(() => {
    if (isVisible) {
      setPuzzleKey(k => k + 1);
    }
  }, [isVisible]);
  
  // Pick 3 random words and generate grid
  const { grid, selectedWords } = useMemo(() => {
    if (!words.length) {
      return { grid: [], selectedWords: [] };
    }
    
    // Shuffle and pick 3
    const shuffled = [...words].sort(() => Math.random() - 0.5);
    const picked = shuffled.slice(0, 3);
    const { grid, placedWords } = generateGrid(picked);
    
    return { grid, selectedWords: placedWords };
  }, [words, puzzleKey]);
  
  if (!isVisible || !grid.length) {
    return null;
  }
  
  return (
    <div className="bg-white rounded-2xl p-5 shadow-2xl border-4 border-green-500">
      <h3 className="text-base font-bold text-center mb-3 text-gray-800">
        Find the Words!
      </h3>
      
      {/* Grid */}
      <div className="bg-gray-100 rounded-xl p-2 mb-3">
        <div 
          className="grid gap-0.5"
          style={{ 
            gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
            width: '288px'
          }}
        >
          {grid.map((row, rowIdx) =>
            row.map((letter, colIdx) => (
              <div
                key={`${rowIdx}-${colIdx}`}
                className="w-6 h-6 flex items-center justify-center bg-white rounded text-sm font-bold text-gray-700 select-none"
              >
                {letter}
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Words to find */}
      <div className="flex flex-wrap justify-center gap-2">
        {selectedWords.map((word, idx) => (
          <span
            key={idx}
            className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold"
          >
            {word}
          </span>
        ))}
      </div>
      
      <p className="text-[10px] text-gray-400 text-center mt-2">
        Horizontal, vertical, or diagonal
      </p>
    </div>
  );
}
